"use client";

import PageHeader from "@/components/layout/PageHeader";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { get, post, put, del } from "../../utils/service";
import { formatMoney, formatLabel, parseApiError, isFinanceUser } from "@/utils/finance";
import { btnPrimary, btnGhost, btnDanger, inputCls, labelCls } from "@/components/finance/Modal";
import Modal from "@/components/finance/Modal";

interface Subscription {
  id: string; title: string; category: string; vendor: string | null; amount: string; currency: string;
  billing_day: number; is_active: boolean; auto_submit: boolean; notes: string | null;
  user?: { id: string; first_name: string; last_name: string };
}
interface Meta { categories: string[]; currencies: string[] }

const empty = { title: "", category: "software", vendor: "", amount: "", currency: "UGX", billing_day: "1", auto_submit: true, notes: "" };

const nextBillingDate = (day: number) => {
  const now = new Date();
  const candidate = new Date(now.getFullYear(), now.getMonth(), day);
  if (candidate < now) candidate.setMonth(candidate.getMonth() + 1);
  return candidate.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
};

export default function ExpenseSubscriptionsPage() {
  const finance = isFinanceUser();
  const [meta, setMeta] = useState<Meta | null>(null);
  const [rows, setRows] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<"add" | { edit: Subscription } | { delete: Subscription } | null>(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const set = (k: string, v: string | boolean) => setForm((f) => ({ ...f, [k]: v }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await get<{ data: { subscriptions: Subscription[] } }>("/expense-subscriptions");
      setRows(r.data.subscriptions);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    get<{ data: Meta }>("/expenses/meta").then((r) => setMeta(r.data)).catch(() => undefined);
    load();
  }, [load]);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true); setError(""); setFieldErrors({});
    try { await fn(); setModal(null); await load(); }
    catch (e) { const f = parseApiError(e); setError(f.message); setFieldErrors(f.fieldErrors); }
    finally { setBusy(false); }
  };

  const openAdd = () => { setForm(empty); setError(""); setFieldErrors({}); setModal("add"); };
  const openEdit = (s: Subscription) => {
    setForm({ title: s.title, category: s.category, vendor: s.vendor ?? "", amount: s.amount, currency: s.currency, billing_day: String(s.billing_day), auto_submit: s.auto_submit, notes: s.notes ?? "" });
    setError(""); setFieldErrors({}); setModal({ edit: s });
  };

  const payload = () => ({ ...form, billing_day: Number(form.billing_day), vendor: form.vendor || undefined, notes: form.notes || undefined });
  const submitAdd = (e: FormEvent) => { e.preventDefault(); run(() => post("/expense-subscriptions", payload())); };
  const submitEdit = (e: FormEvent, s: Subscription) => { e.preventDefault(); run(() => put(`/expense-subscriptions/${s.id}`, payload())); };
  const toggleActive = (s: Subscription) => run(() => put(`/expense-subscriptions/${s.id}`, { is_active: !s.is_active }));
  const remove = (s: Subscription) => run(() => del(`/expense-subscriptions/${s.id}`));

  const Fields = () => (
    <div className="space-y-3">
      <div><label className={labelCls}>Title</label><input className={inputCls} value={form.title} onChange={(e) => set("title", e.target.value)} required maxLength={255} />{fieldErrors.title && <p className="mt-1 text-xs text-red-600">{fieldErrors.title}</p>}</div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className={labelCls}>Category</label><select className={inputCls} value={form.category} onChange={(e) => set("category", e.target.value)}>{meta?.categories.map((c) => <option key={c} value={c}>{formatLabel(c)}</option>)}</select></div>
        <div><label className={labelCls}>Vendor (optional)</label><input className={inputCls} value={form.vendor} onChange={(e) => set("vendor", e.target.value)} /></div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div><label className={labelCls}>Amount</label><input className={inputCls} type="number" step="0.01" min="0.01" value={form.amount} onChange={(e) => set("amount", e.target.value)} required />{fieldErrors.amount && <p className="mt-1 text-xs text-red-600">{fieldErrors.amount}</p>}</div>
        <div><label className={labelCls}>Currency</label><select className={inputCls} value={form.currency} onChange={(e) => set("currency", e.target.value)}>{(meta?.currencies ?? [form.currency]).map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
        <div><label className={labelCls}>Billing day</label><input className={inputCls} type="number" min="1" max="28" value={form.billing_day} onChange={(e) => set("billing_day", e.target.value)} required />{fieldErrors.billing_day && <p className="mt-1 text-xs text-red-600">{fieldErrors.billing_day}</p>}</div>
      </div>
      <label className="flex items-center gap-2 text-xs text-gray-700"><input type="checkbox" checked={form.auto_submit} onChange={(e) => set("auto_submit", e.target.checked)} />Automatically submit the generated expense for approval each month</label>
      <div><label className={labelCls}>Notes (optional)</label><textarea className={inputCls} rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>
    </div>
  );

  return (
    <div className="min-h-screen p-4 sm:p-0">
      <div className="w-full max-w-3xl space-y-5">
        <PageHeader title="Recurring subscriptions" description="Set up a monthly cost once and it's logged as an expense automatically every month">
          <Link to="/expenses" className={btnGhost}>Back to expenses</Link>
          <button className={btnPrimary} onClick={openAdd}>Add subscription</button>
        </PageHeader>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-6 animate-pulse space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-10 bg-gray-100 rounded" />)}</div>
          ) : rows.length ? (
            <ul className="divide-y divide-gray-100">
              {rows.map((s) => (
                <li key={s.id} className="flex items-center justify-between px-5 py-3 gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{s.title}{s.vendor ? ` · ${s.vendor}` : ""}</p>
                    <p className="text-[11px] text-gray-500">{formatLabel(s.category)} · bills on day {s.billing_day} (next {nextBillingDate(s.billing_day)}){finance && s.user ? ` · ${s.user.first_name} ${s.user.last_name}` : ""}{!s.is_active && " · paused"}</p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-sm font-semibold text-gray-900">{formatMoney(s.amount, s.currency)}</span>
                    <button className="text-xs text-gray-600 hover:text-gray-900" onClick={() => openEdit(s)}>Edit</button>
                    <button className="text-xs text-gray-600 hover:text-gray-900" onClick={() => toggleActive(s)}>{s.is_active ? "Pause" : "Resume"}</button>
                    <button className="text-xs text-red-600 hover:text-red-800" onClick={() => setModal({ delete: s })}>Delete</button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="p-10 text-center text-sm text-gray-500">No recurring subscriptions yet.</p>
          )}
        </div>
      </div>

      <Modal open={modal === "add"} onClose={() => setModal(null)} title="Add subscription">
        <form onSubmit={submitAdd} className="space-y-3">
          <Fields />
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex justify-end gap-2"><button type="button" className={btnGhost} onClick={() => setModal(null)}>Cancel</button><button className={btnPrimary} disabled={busy}>{busy ? "Saving…" : "Add"}</button></div>
        </form>
      </Modal>

      <Modal open={typeof modal === "object" && modal !== null && "edit" in modal} onClose={() => setModal(null)} title="Edit subscription">
        {typeof modal === "object" && modal !== null && "edit" in modal && (
          <form onSubmit={(e) => submitEdit(e, modal.edit)} className="space-y-3">
            <Fields />
            {error && <p className="text-xs text-red-600">{error}</p>}
            <div className="flex justify-end gap-2"><button type="button" className={btnGhost} onClick={() => setModal(null)}>Cancel</button><button className={btnPrimary} disabled={busy}>{busy ? "Saving…" : "Save"}</button></div>
          </form>
        )}
      </Modal>

      <Modal open={typeof modal === "object" && modal !== null && "delete" in modal} onClose={() => setModal(null)} title="Delete subscription?"
        footer={typeof modal === "object" && modal !== null && "delete" in modal ? <><button className={btnGhost} onClick={() => setModal(null)}>Keep</button><button className={btnDanger} disabled={busy} onClick={() => remove(modal.delete)}>{busy ? "Deleting…" : "Delete"}</button></> : null}>
        <p className="text-xs text-gray-600">This stops future months from being generated. Expenses already created from it are kept.</p>
        {error && <p className="text-xs text-red-600">{error}</p>}
      </Modal>
    </div>
  );
}
